
import React, { useState, useEffect } from "react";
import { Link, useParams } from "react-router-dom";
import { ArrowLeft, Search, Filter } from "lucide-react";
import { Input } from "@/components/ui/input";
import { Button } from "@/components/ui/button";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/components/ui/select";
import { Popover, PopoverContent, PopoverTrigger } from "@/components/ui/popover";
import { useServices } from "@/hooks/useServices";
import RequestList from '@/components/RequestList';

const RequestCategoryPage = () => {
  const { categoryId } = useParams<{ categoryId: string }>();
  const { categories } = useServices();
  const [searchQuery, setSearchQuery] = useState("");
  const [priceRange, setPriceRange] = useState("");
  const [deliveryMethod, setDeliveryMethod] = useState("");
  
  // Get category by ID
  const category = categories?.find(cat => cat.id === categoryId);
  const categoryName = category ? category.name : "Uncategorized";

  // Reset all filters
  const handleResetFilters = () => {
    setSearchQuery("");
    setPriceRange("");
    setDeliveryMethod("");
  };

  // Category images map - using the same images as CategoryPage
  const categoryImages: Record<string, string> = {
    "Web Development": "https://images.unsplash.com/photo-1461749280684-dccba630e2f6?auto=format&fit=crop&w=1200&q=80",
    "Design": "https://images.unsplash.com/photo-1586717799252-bd134ad00e26?auto=format&fit=crop&w=1200&q=80",
    "Writing": "https://images.unsplash.com/photo-1455390582262-044cdead277a?auto=format&fit=crop&w=1200&q=80",
    "Marketing and Sales": "https://images.unsplash.com/photo-1460925895917-afdab827c52f?auto=format&fit=crop&w=1200&q=80",
    "Game Development": "https://images.unsplash.com/photo-1511512578047-dfb367046420?auto=format&fit=crop&w=1200&q=80",
    "Tutoring": "https://images.unsplash.com/photo-1427504494785-3a9ca7044f45?auto=format&fit=crop&w=1200&q=80",
    "Photography": "https://images.unsplash.com/photo-1606983340126-99ab4feaa64a?auto=format&fit=crop&w=1200&q=80",
    "Music": "https://images.unsplash.com/photo-1493225457124-a3eb161ffa5f?auto=format&fit=crop&w=1200&q=80",
    "Language": "https://images.unsplash.com/photo-1434030216411-0b793f4b4173?auto=format&fit=crop&w=1200&q=80",
    "Health": "https://images.unsplash.com/photo-1559757148-5c350d0d3c56?auto=format&fit=crop&w=1200&q=80",
    "Fashion": "https://images.unsplash.com/photo-1445205170230-053b83016050?auto=format&fit=crop&w=1200&q=80",
    "Business": "https://images.unsplash.com/photo-1507003211169-0a1dd7228f2d?auto=format&fit=crop&w=1200&q=80",
    "Arts": "https://images.unsplash.com/photo-1460661419201-fd4cecdf8a8b?auto=format&fit=crop&w=1200&q=80",
    "Engineering and CAD": "https://images.unsplash.com/photo-1581091226825-a6a2a5aee158?auto=format&fit=crop&w=1200&q=80",
    "Other": "https://images.unsplash.com/photo-1486312338219-ce68d2c6f44d?auto=format&fit=crop&w=1200&q=80"
  };

  // Category descriptions map - using the same descriptions as CategoryPage
  const categoryDescriptions: Record<string, string> = {
    "Web Development": "Find talented developers to create websites, web applications, and digital solutions tailored to your needs.",
    "Design": "Connect with creative designers for logos, graphics, UI/UX design, and visual branding that makes your project stand out.",
    "Writing": "Discover skilled writers for content creation, copywriting, technical documentation, and creative writing projects.",
    "Marketing and Sales": "Get help with marketing strategies, social media management, advertising campaigns, and sales optimization.",
    "Game Development": "Find game developers and designers to bring your gaming ideas to life with professional development skills.",
    "Tutoring": "Connect with experienced tutors and educators for personalized learning in various subjects and skills.",
    "Photography": "Hire professional photographers for events, portraits, product photography, and creative visual content.",
    "Music": "Find musicians, composers, and audio professionals for music production, sound design, and audio services.",
    "Language": "Connect with language experts for translation, interpretation, language learning, and linguistic services.",
    "Health": "Access health and wellness professionals for fitness training, nutrition advice, and wellness coaching.",
    "Fashion": "Find fashion designers, stylists, and consultants for clothing design, personal styling, and fashion advice.",
    "Business": "Get business consulting, strategy development, project management, and professional business services.",
    "Arts": "Connect with artists for custom artwork, illustrations, crafts, and creative artistic projects.",
    "Engineering and CAD": "Find engineering professionals and CAD specialists for technical design and engineering solutions.",
    "Other": "Explore various other professional services and specialized skills for your unique project needs."
  };

  const categoryImage = categoryImages[categoryName] || categoryImages["Other"];
  const categoryDescription = categoryDescriptions[categoryName] || categoryDescriptions["Other"];

  return (
    <div className="min-h-screen bg-white flex flex-col">
      <div className="container mx-auto px-4 py-4 flex-grow">
        <div className="flex items-center mb-3">
          <Link to="/requests" className="flex items-center text-gray-600 hover:text-anyskill-purple mr-4">
            <ArrowLeft className="h-5 w-5 mr-2" />
            Back to Service Requests
          </Link>
          <h1 className="font-poppins font-bold text-xl md:text-2xl">
            Service Requests in <span className="text-anyskill-blue">{categoryName}</span>
          </h1>
        </div>

        {/* Category Hero Section */}
        <div className="mb-6">
          <div className="relative h-48 md:h-64 rounded-lg overflow-hidden">
            <img 
              src={categoryImage} 
              alt={categoryName}
              className="w-full h-full object-cover"
            />
            <div className="absolute inset-0 bg-black bg-opacity-40 flex items-center justify-center">
              <div className="text-center text-white px-4">
                <h2 className="text-2xl md:text-4xl font-bold mb-2">{categoryName}</h2>
                <p className="text-lg md:text-xl opacity-90 max-w-2xl">
                  {categoryDescription}
                </p>
              </div>
            </div>
          </div>
        </div>

        {/* Search and Filters - Now below the image */}
        <div className="flex flex-col gap-2 mb-6">
          <div className="flex flex-col md:flex-row gap-2">
            <div className="relative flex-grow">
              <Search className="absolute left-3 top-1/2 transform -translate-y-1/2 h-4 w-4 text-gray-400" />
              <Input 
                placeholder="Search service requests..." 
                value={searchQuery}
                onChange={(e) => setSearchQuery(e.target.value)}
                className="pl-9 h-10"
              />
            </div>
            
            <div className="flex gap-2 flex-wrap">
              <Select value={priceRange} onValueChange={setPriceRange}>
                <SelectTrigger className="w-[120px] h-10">
                  <SelectValue placeholder="Price Range" />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="any">Any Price</SelectItem>
                  <SelectItem value="0-25">$0 - $25</SelectItem>
                  <SelectItem value="25-50">$25 - $50</SelectItem>
                  <SelectItem value="50-100">$50 - $100</SelectItem>
                  <SelectItem value="100+">$100+</SelectItem>
                </SelectContent>
              </Select>
              
              <Select value={deliveryMethod} onValueChange={setDeliveryMethod}>
                <SelectTrigger className="w-[120px] h-10">
                  <SelectValue placeholder="Delivery" />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="any">Any</SelectItem>
                  <SelectItem value="virtual">Virtual</SelectItem>
                  <SelectItem value="in-person">In-Person</SelectItem>
                  <SelectItem value="both">Both</SelectItem>
                </SelectContent>
              </Select>

              <Popover>
                <PopoverTrigger asChild>
                  <Button variant="outline" size="default" className="h-10">
                    <Filter className="h-4 w-4 mr-2" />
                    Filters
                  </Button>
                </PopoverTrigger>
                <PopoverContent className="w-64">
                  <div className="grid gap-3">
                    <div className="space-y-2">
                      <h4 className="font-medium text-sm">Location</h4>
                      <Select>
                        <SelectTrigger className="h-8">
                          <SelectValue placeholder="Select location" />
                        </SelectTrigger>
                        <SelectContent>
                          <SelectItem value="local">Local Only</SelectItem>
                          <SelectItem value="remote">Remote Only</SelectItem>
                          <SelectItem value="any-location">Any Location</SelectItem>
                        </SelectContent>
                      </Select>
                    </div>
                    <div className="space-y-2">
                      <h4 className="font-medium text-sm">Availability</h4>
                      <Select>
                        <SelectTrigger className="h-8">
                          <SelectValue placeholder="Select availability" />
                        </SelectTrigger>
                        <SelectContent>
                          <SelectItem value="available">Available Now</SelectItem>
                          <SelectItem value="weekdays">Weekdays</SelectItem>
                          <SelectItem value="weekends">Weekends</SelectItem>
                          <SelectItem value="any-time">Any Time</SelectItem>
                        </SelectContent>
                      </Select>
                    </div>
                  </div>
                </PopoverContent>
              </Popover>
              
              {(searchQuery || priceRange || deliveryMethod) && (
                <Button 
                  variant="ghost" 
                  size="default"
                  className="text-gray-500 h-10"
                  onClick={handleResetFilters}
                >
                  Clear
                </Button>
              )}
            </div>
          </div>
        </div>

        {/* Service Requests List for specific category */}
        <div>
          <RequestList 
            categoryId={categoryId} 
            searchQuery={searchQuery}
            priceRange={priceRange}
            deliveryMethod={deliveryMethod}
          />
        </div>
      </div>
    </div>
  );
};

export default RequestCategoryPage;
